import React, { useState } from 'react'
import { Plus, Trash2, ChevronUp, ChevronDown, Pencil, GripVertical, ChevronsUpDown, Mic, Loader2, CheckCircle2 } from 'lucide-react'
import { useLessonBuilder } from '../store/useLessonBuilder'
import { getCardDef, CARD_DEFS } from '../cards/registry'
import { sectionKindMeta } from '../lib/sections'
import { narrateSection } from '../api/lessonBuilderApi'
import AudioSourceButtons from './AudioSourceButtons'
import { isAudioableBlock } from '../../../components/interactive/shared/contentBlocks'
import type { CardType, LessonSection } from '../types/LessonDoc'

// Loại thẻ có nội dung văn bản để lồng tiếng được.
const TEXT_CARD_TYPES = new Set(['text', 'richtext', 'word', 'rich'])

// Bóc văn bản đọc được từ MỘT thẻ — KHÔNG kèm tiêu đề (yêu cầu: không đọc tiêu đề/đầu mục).
function stripHtml(html: string): string {
  const d = document.createElement('div')
  d.innerHTML = html || ''
  return (d.textContent || '').replace(/\s+/g, ' ').trim()
}
function cardText(c: any): string {
  const parts: string[] = []
  if (c.type === 'text') parts.push(c.content || '')
  else if (c.type === 'richtext' || c.type === 'word') parts.push(stripHtml(c.html))
  else if (c.type === 'rich') {
    // Chỉ khối văn xuôi đọc-được; bỏ tiêu đề, câu hỏi, mô phỏng.
    for (const it of c.items || []) {
      if (isAudioableBlock(it)) parts.push(it.content)
    }
  } else if (c.type === 'infographic' && c.caption) parts.push(c.caption)
  return parts.join('. ').replace(/\s+/g, ' ').trim()
}
function cardHasText(c: any): boolean {
  return TEXT_CARD_TYPES.has(c.type) && cardText(c).length > 0
}
// Gộp text cả phần (không tiêu đề) — dùng cho lồng tiếng phần Khởi động.
function sectionText(sec: LessonSection): string {
  return (sec.cards as any[]).map(cardText).filter(Boolean).join('. ').replace(/\s+/g, ' ').trim()
}

// Nút "Tạo Audio" cho RIÊNG một thẻ (phần Hình thành kiến thức) — diễn đạt nội dung thành lời.
const CardVoiceButton: React.FC<{ card: any; lessonId: string }> = ({ card, lessonId }) => {
  const { updateCard } = useLessonBuilder()
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const text = cardText(card)

  const run = async () => {
    if (!text) { setMsg('Thẻ này chưa có nội dung để đọc.'); return }
    setBusy(true); setMsg(null)
    try {
      const r = await narrateSection(lessonId, card.id, text, 'Kore')
      updateCard(card.id, { audioUrl: `${r.url}?t=${Date.now()}`, voiceScript: r.script })
      setMsg('✅ Đã tạo audio. Nhớ Lưu bài.')
    } catch (e: any) {
      setMsg(`Lỗi: ${e?.message || 'không tạo được audio'}`)
    } finally { setBusy(false) }
  }

  return (
    <div className="rounded-xl border border-orange-100 bg-orange-50/60 p-2.5">
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={run} disabled={busy || !text}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-orange-500 text-white text-xs font-bold hover:bg-orange-600 disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Mic size={14} />}
          {card.audioUrl ? 'Tạo lại Audio' : 'Tạo Audio diễn đạt nội dung'}
        </button>
        {card.audioUrl && !busy && (
          <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600"><CheckCircle2 size={13} /> Đã có audio</span>
        )}
        <span className="text-[11px] text-gray-400">~1 điểm AI/lần · không đọc tiêu đề</span>
        <span className="text-[11px] text-gray-300">hoặc</span>
        <AudioSourceButtons lessonId={lessonId} sectionId={card.id} currentUrl={card.audioUrl} onAudio={(p) => updateCard(card.id, p)} disabled={busy} />
      </div>
      {card.audioUrl && <audio controls preload="none" src={card.audioUrl} className="mt-2 w-full h-9" />}
      {msg && <p className="mt-1.5 text-[11px] text-gray-600">{msg}</p>}
    </div>
  )
}

// Thanh "Tạo lồng tiếng" — chỉ hiện ở phần Khởi động (warmup) có nội dung văn bản.
const VoiceBar: React.FC<{ sec: LessonSection; lessonId: string }> = ({ sec, lessonId }) => {
  const { patchSection } = useLessonBuilder()
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const text = sectionText(sec)

  const run = async () => {
    if (!text) { setMsg('Phần này chưa có nội dung văn bản để đọc.'); return }
    setBusy(true); setMsg(null)
    try {
      const r = await narrateSection(lessonId, sec.id, text, 'Kore', sec.kind || 'warmup')
      patchSection(sec.id, { audioUrl: `${r.url}?t=${Date.now()}`, voiceScript: r.script })
      setMsg('✅ Đã tạo lồng tiếng. Nhớ bấm Lưu bài để giữ lại.')
    } catch (e: any) {
      setMsg(`Lỗi: ${e?.message || 'không tạo được lồng tiếng'}`)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mb-3 rounded-xl border border-orange-100 bg-orange-50/60 p-2.5">
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={run}
          disabled={busy || !text}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-orange-500 text-white text-xs font-bold hover:bg-orange-600 disabled:opacity-50"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Mic size={14} />}
          {sec.audioUrl ? 'Tạo lại lồng tiếng' : 'Tạo lồng tiếng (đọc cả công thức)'}
        </button>
        {sec.audioUrl && !busy && (
          <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600"><CheckCircle2 size={13} /> Đã có lồng tiếng</span>
        )}
        <span className="text-[11px] text-gray-400">~1 điểm AI/lần</span>
        <span className="text-[11px] text-gray-300">hoặc</span>
        <AudioSourceButtons lessonId={lessonId} sectionId={sec.id} currentUrl={sec.audioUrl} onAudio={(p) => patchSection(sec.id, p)} disabled={busy} />
      </div>
      {sec.audioUrl && <audio controls preload="none" src={sec.audioUrl} className="mt-2 w-full h-9" />}
      {msg && <p className="mt-1.5 text-[11px] text-gray-600">{msg}</p>}
    </div>
  )
}

// Combobox chọn loại thẻ để thêm vào MỘT phần (thay cho kéo-thả từ palette dài bên trái).
const AddCardMenu: React.FC<{ onPick: (t: CardType) => void }> = ({ onPick }) => {
  const [open, setOpen] = useState(false)
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-dashed border-gray-200 text-gray-500 font-semibold hover:border-blue-300 hover:text-blue-600"
      >
        <Plus size={16} /> Thêm thẻ vào phần này <ChevronsUpDown size={14} className="opacity-60" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute z-30 left-0 right-0 mt-1 max-h-80 overflow-auto bg-white rounded-xl border border-gray-200 shadow-xl p-1">
            {CARD_DEFS.map((def) => {
              const Icon = def.icon
              return (
                <button
                  key={def.type}
                  onClick={() => { onPick(def.type); setOpen(false) }}
                  className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-blue-50/70 text-left"
                >
                  <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${def.color}`}><Icon size={16} /></span>
                  <span className="min-w-0">
                    <span className="block text-sm font-bold text-gray-800">{def.label}</span>
                    <span className="block text-[11px] text-gray-400 truncate">{def.description}</span>
                  </span>
                </button>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

// Canvas giữa: mỗi PHẦN là vùng thả thẻ; thẻ trong phần kéo được + sửa nội dung inline.
const SectionCanvas: React.FC = () => {
  const {
    lesson,
    selectedCardId,
    addSection,
    renameSection,
    removeSection,
    moveSection,
    reorderSection,
    addCard,
    updateCard,
    removeCard,
    moveCard,
    moveCardToSection,
    selectCard,
  } = useLessonBuilder()
  const [dragOver, setDragOver] = useState<string | null>(null)
  const [secDragId, setSecDragId] = useState<string | null>(null)        // Phần đang kéo
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())     // Phần đang thu gọn

  const toggleCollapse = (id: string) =>
    setCollapsed((prev) => {
      const n = new Set(prev)
      n.has(id) ? n.delete(id) : n.add(id)
      return n
    })
  const allCollapsed = lesson.sections.length > 0 && lesson.sections.every((s) => collapsed.has(s.id))
  const toggleCollapseAll = () =>
    setCollapsed(allCollapsed ? new Set() : new Set(lesson.sections.map((s) => s.id)))

  const handleDrop = (sectionId: string, si: number, e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(null)
    // Ưu tiên: kéo-thả SẮP XẾP LẠI PHẦN
    const secMove = e.dataTransfer.getData('text/section-move')
    if (secMove) {
      if (secMove !== sectionId) reorderSection(secMove, si)
      setSecDragId(null)
      return
    }
    // Thêm thẻ mới / chuyển thẻ vào phần
    const newType = e.dataTransfer.getData('text/card-new')
    if (newType) {
      addCard(sectionId, newType as CardType)
      return
    }
    const moveId = e.dataTransfer.getData('text/card-move')
    if (moveId) moveCardToSection(moveId, sectionId)
  }

  return (
    <div className="space-y-4">
      {lesson.sections.length > 0 && (
        <div className="flex justify-end -mb-1">
          <button onClick={toggleCollapseAll} className="flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-blue-600">
            <ChevronsUpDown size={14} /> {allCollapsed ? 'Mở tất cả' : 'Thu gọn tất cả'}
          </button>
        </div>
      )}
      {lesson.sections.map((sec, si) => {
        const isCol = collapsed.has(sec.id)
        return (
        <div
          key={sec.id}
          onDragOver={(e) => {
            e.preventDefault()
            setDragOver(sec.id)
          }}
          onDragLeave={() => setDragOver((d) => (d === sec.id ? null : d))}
          onDrop={(e) => handleDrop(sec.id, si, e)}
          className={`rounded-2xl border-2 p-4 transition-colors ${
            secDragId === sec.id ? 'opacity-50' : ''
          } ${dragOver === sec.id ? 'border-blue-400 bg-blue-50/50' : 'border-gray-100 bg-white'}`}
        >
          {/* Header phần */}
          <div className={`flex items-center gap-1.5 ${isCol ? '' : 'mb-3'}`}>
            <span
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData('text/section-move', sec.id)
                e.dataTransfer.effectAllowed = 'move'
                setSecDragId(sec.id)
              }}
              onDragEnd={() => setSecDragId(null)}
              title="Kéo để sắp xếp lại phần"
              className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 shrink-0"
            >
              <GripVertical size={18} />
            </span>
            <button
              onClick={() => toggleCollapse(sec.id)}
              title={isCol ? 'Mở phần' : 'Thu gọn phần'}
              className="p-1 text-gray-400 hover:text-blue-600 shrink-0"
            >
              <ChevronDown size={16} className={`transition-transform ${isCol ? '-rotate-90' : ''}`} />
            </button>
            <span className="w-7 h-7 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center shrink-0">
              {si + 1}
            </span>
            <input
              value={sec.title}
              onChange={(e) => renameSection(sec.id, e.target.value)}
              className="flex-1 min-w-0 bg-transparent text-base font-black text-blue-900 outline-none border-b border-transparent focus:border-blue-200"
            />
            {isCol && <span className="text-[11px] font-semibold text-gray-400 shrink-0">{sec.cards.length} thẻ</span>}
            <button onClick={() => moveSection(sec.id, -1)} disabled={si === 0} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30">
              <ChevronUp size={16} />
            </button>
            <button onClick={() => moveSection(sec.id, 1)} disabled={si === lesson.sections.length - 1} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30">
              <ChevronDown size={16} />
            </button>
            <button
              onClick={() => {
                if (sec.cards.length === 0 || confirm('Xoá phần này và các thẻ bên trong?')) removeSection(sec.id)
              }}
              className="p-1 text-gray-400 hover:text-red-600"
            >
              <Trash2 size={16} />
            </button>
          </div>

          {/* Thân phần (ẩn khi thu gọn) */}
          {!isCol && (<>
          {/* Lồng tiếng — chỉ phần Khởi động */}
          {sectionKindMeta(sec).kind === 'warmup' && <VoiceBar sec={sec} lessonId={lesson.id} />}
          {/* Thẻ trong phần */}
          {sec.cards.length === 0 ? (
            <div className="py-5 text-center text-sm text-gray-400">Chưa có thẻ — chọn loại thẻ bên dưới để thêm.</div>
          ) : (
            <div className="space-y-2">
              {sec.cards.map((card, ci) => {
                const def = getCardDef(card.type)
                const Icon = def.icon
                const open = card.id === selectedCardId
                const Editor = def.Editor
                return (
                  <div
                    key={card.id}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData('text/card-move', card.id)
                      e.dataTransfer.effectAllowed = 'move'
                    }}
                    className={`rounded-xl border ${open ? 'border-blue-300' : 'border-gray-100'} bg-white`}
                  >
                    <div className="flex items-center gap-2 p-2.5">
                      <GripVertical size={16} className="text-gray-300 cursor-grab active:cursor-grabbing shrink-0" />
                      <span className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${def.color}`}>
                        <Icon size={15} />
                      </span>
                      <button onClick={() => selectCard(open ? null : card.id)} className="flex-1 min-w-0 text-left">
                        <span className="block text-sm font-semibold text-gray-800 truncate">{card.title || def.label}</span>
                        <span className="block text-[11px] text-gray-400">{def.label}</span>
                      </button>
                      <button onClick={() => selectCard(open ? null : card.id)} className="p-1 text-gray-400 hover:text-blue-600">
                        <Pencil size={15} />
                      </button>
                      <button onClick={() => moveCard(card.id, -1)} disabled={ci === 0} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30">
                        <ChevronUp size={15} />
                      </button>
                      <button onClick={() => moveCard(card.id, 1)} disabled={ci === sec.cards.length - 1} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30">
                        <ChevronDown size={15} />
                      </button>
                      <button onClick={() => removeCard(card.id)} className="p-1 text-gray-400 hover:text-red-600">
                        <Trash2 size={15} />
                      </button>
                    </div>

                    {/* Trình sửa nội dung inline */}
                    {open && (
                      <div className="border-t border-gray-100 p-3 space-y-3 bg-gray-50/40">
                        <div>
                          <label className="text-xs font-bold text-gray-500 uppercase">Tiêu đề thẻ (tuỳ chọn)</label>
                          <input
                            className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200"
                            value={card.title || ''}
                            onChange={(e) => updateCard(card.id, { title: e.target.value })}
                          />
                        </div>
                        <Editor card={card} lessonId={lesson.id} onChange={(patch) => updateCard(card.id, patch)} />
                        {/* Lồng tiếng mức-thẻ — chỉ phần Hình thành kiến thức, thẻ text ĐƠN (không phải rich;
                            thẻ rich đã có nút Audio cho TỪNG khối ở trên) */}
                        {sectionKindMeta(sec).kind === 'content' && card.type !== 'rich' && cardHasText(card) && (
                          <CardVoiceButton card={card} lessonId={lesson.id} />
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {/* Combobox thêm thẻ vào phần này */}
          <div className="mt-2">
            <AddCardMenu onPick={(t) => addCard(sec.id, t)} />
          </div>
          </>)}
        </div>
        )
      })}

      <button
        onClick={() => addSection()}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border-2 border-dashed border-gray-200 text-gray-500 font-semibold hover:border-blue-300 hover:text-blue-600"
      >
        <Plus size={18} /> Thêm phần
      </button>
    </div>
  )
}

export default SectionCanvas
